import React from 'react';
import {Clock, MapPin, Store, Ruler} from 'lucide-react';
import type {WorkSession} from '../models/session.model';

interface SessionSummaryCardProps {
  session: WorkSession | null;
  loading?: boolean;
}

function formatTime (d: Date): string {
  return `${d.getHours().toString().padStart(2, '0')}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
}

function formatDuration (secs: number | null, fallback: number): string {
  const totalSeconds = secs ?? fallback;
  if (totalSeconds <= 0) return '—';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${h} س ${m} د`;
}

function formatDistanceKm (meters: number | null): string {
  if (meters === null || meters === undefined) return '—';
  if (meters < 1) return '0 كم';
  return `${(meters / 1000).toFixed(2)} كم`;
}

export const SessionSummaryCard: React.FC<SessionSummaryCardProps> = ({session, loading}) => {
  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-center text-gray-500">
        جارٍ التحميل...
      </div>
    );
  }

  if (!session) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-gray-500">
        لا توجد جلسة لعرضها
      </div>
    );
  }

  const durationFallback =
    session.clockOutAt !== null
      ? Math.floor((session.clockOutAt.getTime() - session.clockInAt.getTime()) / 1000)
      : Math.floor((Date.now() - session.clockInAt.getTime()) / 1000);

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="font-bold text-lg">ملخص اليوم</div>
        <div
          className={`rounded-full px-3 py-1 text-sm font-medium ${
            session.status === 'active'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-700'
          }`}
        >
          {session.status === 'active' ? 'جلسة نشطة' : 'جلسة منتهية'}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat
          icon={<Clock className="h-4 w-4 text-green-600" />}
          label="بدء الدوام"
          value={formatTime(session.clockInAt)}
        />
        <Stat
          icon={<Clock className="h-4 w-4 text-red-600" />}
          label="نهاية الدوام"
          value={session.clockOutAt ? formatTime(session.clockOutAt) : '—'}
        />
        <Stat
          icon={<Ruler className="h-4 w-4 text-blue-600" />}
          label="المسافة"
          value={formatDistanceKm(session.totalDistanceMeters)}
        />
        <Stat
          icon={<Store className="h-4 w-4 text-purple-600" />}
          label="المتاجر المُزارة"
          value={session.totalStoresVisited.toString()}
        />
        <Stat
          icon={<MapPin className="h-4 w-4 text-gray-600" />}
          label="مدة العمل"
          value={formatDuration(session.durationSeconds, durationFallback)}
        />
      </div>
    </div>
  );
};

interface StatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const Stat: React.FC<StatProps> = ({icon, label, value}) => (
  <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
    <div className="flex items-center gap-2 text-sm text-gray-600">
      {icon}
      <span>{label}</span>
    </div>
    <div className="mt-1 text-lg font-semibold">{value}</div>
  </div>
);
